import { useState } from 'react';
import { Thumb } from '../components/ImagePick';
import { batches as Api, type Batch } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim, SkeletonRows } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { StateTag } from '../components/Field';
import { BatchForm } from './BatchForm';

const day = (v: string) => {
  if (!v) return '—';
  const d = new Date(`${v}T00:00:00Z`);
  return isNaN(d.valueOf()) ? v
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

export function Batches() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [editing, setEditing] = useState<Batch | 'new' | null>(null);
  const items = data?.items ?? [];

  const sel = useSelection(items);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Live batches</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={210} h={12} style={{ marginTop: 3 }} />
              : items.length ? `${items.length} batch${items.length === 1 ? '' : 'es'}`
              : 'No batch has been scheduled.'}
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditing('new')}>New batch</button>
      </div>

      {error ? <div className="err">Could not load the batches — {error.message}</div> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <BulkActions
          path="/batches" noun="batch"
          rows={sel.picked.map((b) => ({ id: b.id, label: String(b.name) }))}
          actions={[['publish', 'Publish'], ['unpublish', 'Unpublish']]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} /><th>Batch</th><th>Starts</th><th>Seats</th><th>Fee</th><th>Sessions</th><th>State</th><th /></tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={8} tick />
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="muted" style={{ padding: '26px 8px' }}>
                  Nothing scheduled. Until a batch is published, the live-classes page says so rather than
                  showing a date nobody set.
                </td></tr>
              ) : items.map((b) => {
                const left = Math.max(0, (b.seats_total || 0) - (b.seats_sold || 0));
                return (
                  <tr key={b.id} style={sel.isOn(b.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(b.id)} label={String(b.name)}
                    onChange={(v) => sel.toggle(b.id, v)} />
                    <td>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <Thumb src={b.cover_path} width={60} />
                        <div>
                          <div style={{ fontWeight: 700 }}>{b.name}</div>
                          {b.subtitle ? <div style={{ fontSize: 11.5, color: 'var(--tx3)' }}>{b.subtitle}</div> : null}
                        </div>
                      </div>
                    </td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {day(b.starts_on)}{b.start_time ? <div style={{ fontSize: 11.5 }}>{b.start_time}</div> : null}
                    </td>
                    <td>
                      {b.seats_total
                        ? <>{b.seats_sold}/{b.seats_total}
                            <div style={{ fontSize: 11, color: left ? 'var(--tx3)' : 'var(--down)' }}>
                              {left ? `${left} left` : 'sold out'}
                            </div></>
                        : <span className="muted">not set</span>}
                    </td>
                    <td>{b.fee ? `₹${b.fee.toLocaleString('en-IN')}` : <span className="muted">—</span>}</td>
                    <td className="muted">{b.session_count ?? b.sessions?.length ?? 0}</td>
                    <td><StateTag state={b.state} /></td>
                    <td className="right">
                      <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                        type="button" onClick={() => setEditing(b)}>Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <BatchForm
          batch={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}
